import type { FriendRef } from "@rietz/shared";
import type { FriendsMsg, GameSocket, InvResultMsg, PartyStateMsg } from "../net/socket.js";
import {
  setFriends,
  setParty,
  setPartyInvite,
  setSocialError,
} from "../ui/store.js";
import type { OpIds } from "./opIds.js";

/**
 * Friends and the party, as the client believes them — exactly as the server
 * last described them. The third controller drawing from the connection's one
 * `opId` counter, and the only one with NO optimistic layer: every one of the
 * seven verbs draws NOTHING until the state the server pushes afterwards
 * arrives, since the answer depends on facts only the server holds. This owns
 * the seven sends, WHICH ids are ours (so a stash drag's `invResult` is not
 * mistaken for a social refusal), and the three pushed messages. The refusal
 * (`HudState.socialError`) is the only explanation the player gets, kept until
 * the next verb replaces it.
 */

export interface SocialControllerOptions {
  /** `null` offline: an `?offline=1` session has no meta layer, and no friends. */
  socket: GameSocket | null;
  /** THE connection's op-id counter, shared with the two inventory controllers. */
  opIds: OpIds;
}

export class SocialController {
  private readonly o: SocialControllerOptions;

  /**
   * Ids of social verbs the server has not answered yet. A Set: the answer is a
   * verdict, not a fold.
   */
  private readonly pending = new Set<number>();

  constructor(opts: SocialControllerOptions) {
    this.o = opts;
  }

  // -----------------------------------------------------------------------
  // UiActions (the friends tab's half — `main.ts` routes by screen)
  // -----------------------------------------------------------------------

  /** Ask someone to be friends, by username. */
  friendRequest(username: string): void {
    this.spend((socket, opId) => socket.sendFriendRequest(opId, username));
  }

  /** Accept or decline one INCOMING request. */
  friendRespond(username: string, accept: boolean): void {
    this.spend((socket, opId) => socket.sendFriendRespond(opId, username, accept));
  }

  /** Unfriend, or withdraw a request we sent — one gesture, one verb. */
  friendRemove(username: string): void {
    this.spend((socket, opId) => socket.sendFriendRemove(opId, username));
  }

  /** Ask a friend into the squad, creating one with us as leader if needed. */
  partyInvite(username: string): void {
    this.spend((socket, opId) => socket.sendPartyInvite(opId, username));
  }

  /**
   * Answer THE pending invite. The prompt comes down on the send, whichever way
   * — declining has nothing to wait for, and an acceptance is superseded by the
   * `partyState` that follows.
   */
  partyRespond(accept: boolean): void {
    setPartyInvite(null);
    this.spend((socket, opId) => socket.sendPartyRespond(opId, accept));
  }

  /** Walk out of the squad. */
  partyLeave(): void {
    this.spend((socket, opId) => socket.sendPartyLeave(opId));
  }

  /** Throw a member out. Leader only — the server checks, and so does the tab. */
  partyKick(username: string): void {
    this.spend((socket, opId) => socket.sendPartyKick(opId, username));
  }

  // -----------------------------------------------------------------------
  // Server messages
  // -----------------------------------------------------------------------

  /** THE friends state, whole. Replaces whatever we believed. */
  onFriends(msg: FriendsMsg): void {
    setFriends({ friends: msg.friends, incoming: msg.incoming, outgoing: msg.outgoing });
  }

  /**
   * THE party, whole, or `null` for "you are in none". A party in hand answers
   * any invite still on screen, so the prompt comes down with it.
   */
  onPartyState(party: PartyStateMsg["party"]): void {
    setParty(party);
    if (party !== null) setPartyInvite(null);
  }

  /** A prompt, and nothing more: the newest invite replaces any older one. */
  onPartyInvited(from: FriendRef): void {
    setPartyInvite(from);
  }

  /**
   * The verdict on one op — the same message and id space as the inventories.
   * An id we did not issue is another controller's; ours either succeeded (the
   * stale refusal goes) or failed with the only reason the player will hear.
   */
  onInvResult(msg: InvResultMsg): void {
    if (!this.pending.delete(msg.opId)) return;
    setSocialError(msg.ok ? null : { opId: msg.opId, reason: msg.reason ?? "" });
  }

  /**
   * Disconnect: forget everything the old connection said. The reconnect's
   * `hello` is answered with fresh copies.
   */
  reset(): void {
    this.pending.clear();
    setFriends(null);
    setParty(null);
    setPartyInvite(null);
    setSocialError(null);
  }

  // -----------------------------------------------------------------------

  /**
   * One id, one message, one refusal forgotten — so the error line reads as
   * "why THAT click did nothing". No socket, no id spent.
   */
  private spend(send: (socket: GameSocket, opId: number) => void): void {
    const socket = this.o.socket;
    if (socket === null) return;
    setSocialError(null);
    const opId = this.o.opIds.next();
    this.pending.add(opId);
    send(socket, opId);
  }
}
